import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ModelMap } from './model/models';

export type SeeqErrorData = {
  statusMessage: string;
  errorType?: string;
  errorCategory?: string;
  inaccessible?: string[];
};

export type SeeqError = {
  data: SeeqErrorData;
  status?: number;
  xhrStatus?: string;
};

export type SeeqRequestConfig = Pick<
  AxiosRequestConfig,
  'method' | 'url' | 'params' | 'headers' | 'data' | 'signal'
>;

export type SeeqRequestOverrides = Partial<AxiosRequestConfig> & {
  /** Override the axios instance for this call. */
  axiosInstance?: AxiosInstance;
  /** Cancel group name for Seeq cancellation coordination. */
  cancellationGroup?: string;
  /** Request server-side cancellation. */
  cancelOnServer?: boolean;
  /** Skip the loading bar for this request. */
  ignoreLoadingBar?: boolean;
  /** Marks a request as a refetch for UI/telemetry. */
  refetching?: boolean;
  /** Skip pending-request tracking for this call. */
  silent?: boolean;
};

export type SeeqResponse<T = unknown> = Pick<AxiosResponse<T>, 'data' | 'status' | 'headers'>;

export type FactoryOverrides = {
  [K in keyof ModelMap]?: Partial<ModelMap[K]> | ((count: number) => Partial<ModelMap[K]>);
};

export type SerializeParams = (params: Record<string, unknown>) => string;
export type UseAxiosInstance = () => AxiosInstance;

type RuntimeConfig = {
  baseUrl: string;
  getAxiosInstance: () => AxiosInstance;
  useAxiosInstance: UseAxiosInstance;
  factoryOverrides?: FactoryOverrides;
  serializeParams?: SerializeParams;
};

type RuntimeConfigInternal = RuntimeConfig & { serializeParams: SerializeParams };

let runtime: RuntimeConfigInternal | null = null;

const defaultSerializeParams: SerializeParams = (params) => {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null) {
          searchParams.append(key, String(item));
        }
      }
      continue;
    }

    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }

  return searchParams.toString();
};

export function initSdk(config: RuntimeConfig) {
  runtime = {
    ...config,
    serializeParams: config.serializeParams ?? defaultSerializeParams,
  };
}

export function getRuntime() {
  if (!runtime) {
    throw new Error('SDK not initialized. Call initSdk(...) first.');
  }
  return runtime;
}

export function appendParams(url: string, params?: Record<string, unknown>) {
  if (!params || Object.keys(params).length === 0) {
    return url;
  }

  const searchParams = getRuntime().serializeParams(params);
  if (!searchParams) {
    return url;
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${searchParams}`;
}
